import { useCallback, useSyncExternalStore } from "react";

/**
 * KEY-774: the tab a reader last chose, per kind of card, so moving to another action point (or section, or
 * item) keeps them on Items, or Guidance, or wherever they were — instead of every card opening on its default.
 *
 * Kept OUTSIDE React state on purpose. The panels don't live long enough to hold it: the Dynamic and 3D
 * workspaces key the cards container on the selection and remount every panel on every pick, moving between
 * levels unmounts one panel and mounts another, and switching dials remounts the lot. A module-level value
 * survives all three.
 *
 * Mirrored to sessionStorage, NOT localStorage: this product runs on shared tablets (KEY-457 — see the
 * hide-empty comment in DialWorkspace), where a localStorage key is inherited by whoever picks the tablet up
 * next. A session's worth is what "remember where I was" means anyway. A tab id isn't personal data, so the
 * mirror carries nothing worth protecting — it only lets a page reload keep the reader's place.
 *
 * Storage is best-effort: private windows and locked-down browsers throw on access, and the in-memory value
 * carries on regardless.
 */
export type TabMemoryKind = "actionPoint" | "section" | "item";

const memory = new Map<TabMemoryKind, string>();
const listeners = new Set<() => void>();

function storageKey(kind: TabMemoryKind): string {
  return `kd.lastTab.${kind}`;
}

/** The tab last chosen for this kind of card, or null when nothing has been. */
export function rememberedTab<T extends string>(kind: TabMemoryKind): T | null {
  const held = memory.get(kind);
  if (held !== undefined) {
    return held as T;
  }
  try {
    const stored = window.sessionStorage.getItem(storageKey(kind));
    // Only a non-empty string is a tab id; anything else is noise from some other writer.
    if (typeof stored === "string" && stored.length > 0) {
      memory.set(kind, stored);
      return stored as T;
    }
  } catch {
    // no storage — nothing remembered from before this page load
  }
  return null;
}

/** Records a choice. Callers validate against their own tab list on read, so any id is accepted here. */
export function rememberTab(kind: TabMemoryKind, tab: string): void {
  if (memory.get(kind) === tab) {
    return;
  }
  memory.set(kind, tab);
  try {
    window.sessionStorage.setItem(storageKey(kind), tab);
  } catch {
    // memory alone still carries it for this page
  }
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The remembered tab for `kind` and a setter that writes through. Every mounted user of a kind sees a write,
 * so two panels of the same kind can't disagree. The value is NOT validated: a card checks it against the tabs
 * it actually shows and falls back to its own default for display, leaving the stored choice alone so it comes
 * back on the next card that has that tab.
 */
export function useRememberedTab<T extends string>(kind: TabMemoryKind): [T | null, (tab: T) => void] {
  const value = useSyncExternalStore(
    subscribe,
    () => rememberedTab<T>(kind),
    () => null,
  );
  const remember = useCallback((tab: T) => rememberTab(kind, tab), [kind]);
  return [value, remember];
}

/** Tests only: forgets every remembered tab, so one test's click can't leak into the next. */
export function __resetRememberedTabs(): void {
  memory.clear();
}
